<template>
	<div class="relation-config">
		<div class="relation-config__scroll">
			<section class="page-title-bar">
				<div class="page-title-bar__glow page-title-bar__glow--left"></div>
				<div class="page-title-bar__glow page-title-bar__glow--right"></div>

				<button class="back-btn" type="button" @click="handleBack">
					<i class="el-icon-back"></i>
				</button>
				<div class="page-title-bar__content">
					<h1>关联设备配置</h1>
					<p>{{ regulatorMetaText }}</p>
				</div>
			</section>

			<section class="notice-card">
				<i class="el-icon-info"></i>
				<div>
					<strong>配置说明</strong>
					<p>
						请为该调压器关联调压柜内的采集设备，并绑定对应的采集器通道。已绑定的通道数据将作为调压器的运行监测数据，用于异常分析与特征画像计算。
					</p>
				</div>
			</section>

			<section class="config-section collector-section">
				<div class="section-title">
					<span class="section-icon section-icon--purple">
						<i class="el-icon-cpu"></i>
					</span>
					<span>选择采集器</span>
				</div>

				<div
					class="collector-select"
					role="button"
					tabindex="0"
					@click="openCollectorDialog"
					@keydown.enter="openCollectorDialog"
				>
					<div class="collector-main">
						<span class="collector-name">{{ selectedCollector.name }}</span>
						<span class="collector-code">{{ selectedCollector.code }}</span>
					</div>
					<div class="collector-meta">
						<span>安装位置：{{ selectedCollector.installLocation }}</span>
						<span>通道数量：{{ selectedCollector.channelCount }}通道</span>
						<span>已用通道：{{ selectedCollector.usedChannelCount }}个</span>
						<i class="el-icon-arrow-down"></i>
					</div>
				</div>
			</section>

			<section class="config-section device-section">
				<div class="section-header">
					<div class="section-title">
						<span class="section-icon section-icon--blue">
							<i class="el-icon-connection"></i>
						</span>
						<span>关联设备配置</span>
					</div>
					<el-button class="add-device-btn" type="primary" icon="el-icon-plus" @click="handleAddDevice">
						添加设备
					</el-button>
				</div>

				<div class="device-list" v-loading="subDeviceLoading">
					<div v-if="!devices.length" class="device-empty">
						<gc-empty />
					</div>
					<el-form
						v-for="(device, index) in devices"
						:key="device.id"
						:ref="`deviceForm-${device.id}`"
						:model="device"
						label-position="top"
						class="device-card"
					>
						<header class="device-card__header">
							<div class="device-card__title">子设备{{ index + 1 }}</div>
							<div class="device-actions">
								<el-tooltip content="保存" placement="top">
									<el-button
										class="device-save-btn"
										type="primary"
										icon="el-icon-check"
										circle
										:loading="device.saveLoading"
										@click="handleSaveDevice(device)"
									></el-button>
								</el-tooltip>
								<el-tooltip v-if="canDeleteDevice(device)" content="删除" placement="top">
									<el-button
										class="delete-btn"
										icon="el-icon-delete"
										circle
										:loading="device.deleteLoading"
										@click="handleRemoveDevice(device, index)"
									></el-button>
								</el-tooltip>
							</div>
						</header>

						<div class="device-form">
							<el-form-item
								class="field"
								label="设备类型"
								prop="type"
								:rules="[{ required: true, message: '请选择设备类型', trigger: 'change' }]"
							>
								<el-select
									v-model="device.type"
									:loading="deviceTypeLoading"
									:disabled="isSavedDevice(device)"
									placeholder="请选择设备类型"
									@change="handleDeviceTypeChange(device)"
								>
									<el-option
										v-for="item in deviceTypeOptions"
										:key="item.value"
										:label="item.label"
										:value="item.value"
									></el-option>
								</el-select>
							</el-form-item>
							<el-form-item
								class="field"
								label="外观编号"
								prop="designNo"
								:rules="[{ required: true, message: '请输入外观编号', trigger: 'blur' }]"
							>
								<el-input v-model="device.designNo" placeholder="请输入外观编号"></el-input>
							</el-form-item>
							<el-form-item
								class="field"
								label="通道选择"
								prop="slaveNo"
								:rules="[{ required: true, message: '请选择通道', trigger: 'change' }]"
							>
								<el-select
									v-model="device.slaveNo"
									:disabled="isSavedDevice(device)"
									placeholder="请选择通道"
								>
									<el-option
										v-for="channel in channels"
										:key="channel.code"
										:label="channel.name"
										:value="channel.code"
										:disabled="isChannelSelectedByOtherDevice(channel.code, device)"
									></el-option>
								</el-select>
							</el-form-item>
						</div>

						<div class="parameter-config">
							<div class="parameter-config__title">参数配置</div>
							<div class="parameter-grid" v-loading="device.parameterLoading">
								<el-form-item
									v-for="item in device.parameterFields"
									:key="item.attributeKey"
									class="parameter-field"
									:label="item.name"
									:prop="item.attributeKey"
									:rules="item.rules"
								>
									<el-select
										v-if="item.valueType === '2'"
										v-model="device[item.attributeKey]"
										placeholder="请选择"
									>
										<el-option
											v-for="(value, key) in item.interactiveTranslationDes"
											:key="key"
											:value="key"
											:label="value"
										></el-option>
									</el-select>
									<el-date-picker
										v-else-if="item.valueType === '1'"
										v-model="device[item.attributeKey]"
										type="date"
										value-format="yyyy-MM-dd"
										placeholder="选择日期"
									></el-date-picker>
									<el-input v-else v-model="device[item.attributeKey]" placeholder="请输入">
										<i slot="suffix" v-if="item.unitname">
											{{ item.unitname }}
										</i>
									</el-input>
								</el-form-item>
							</div>
						</div>
					</el-form>
				</div>
			</section>

			<section class="config-section channel-section">
				<div class="section-title">
					<span class="section-icon section-icon--green">
						<i class="el-icon-set-up"></i>
					</span>
					<span>可用通道状态</span>
				</div>

				<div class="channel-list">
					<div
						v-for="channel in channelStatusList"
						:key="channel.code"
						class="channel-card"
						:class="{ 'is-used': channel.used }"
					>
						<div class="channel-card__top">
							<strong>{{ channel.name }}</strong>
							<span class="status-tag" :class="channel.used ? 'status-tag--blue' : 'status-tag--success'">
								{{ channel.used ? '已用' : '可用' }}
							</span>
						</div>
						<p>{{ channel.device }}</p>
						<small>{{ channel.parameter }}</small>
					</div>
				</div>
			</section>
		</div>

		<footer class="footer-actions">
			<el-button class="cancel-btn" @click="handleCancel">取消</el-button>
			<el-button class="save-btn" type="primary" :loading="saveConfigLoading" @click="handleSave">
				保存配置
			</el-button>
		</footer>

		<CollectorSelectDialog
			:show.sync="collectorDialogVisible"
			:collectors="collectors"
			:loading="collectorLoading"
			:selected-id="selectedCollector.id"
			@select="handleCollectorSelect"
		/>
	</div>
</template>

<script>
import CollectorSelectDialog from './components/CollectorSelectDialog.vue'
import {
	apiBindPressureRegulatorCollector,
	apiGetPressureRegulatorCollector,
	apiGetPressureRegulatorDetail,
	apiGetPressureRegulatorDeviceTypes,
	apiGetPressureRegulatorSubDevices,
	apiGetUnboundPressureRegulatorCollectors,
} from '@/apis/pressureRegulatorManage.api'
import { apiAddDevice, apiChangeDevice, apiDeleteDevice, apiEditDeviceView } from '@/apis/account.api'
import { isBlank } from '@/utils/validate'

const deviceTemplate = {
	id: 6,
	name: '新增采集设备',
	designNo: '',
	type: '',
	slaveNo: '',
	unit: '',
	rangeMax: '',
	rangeMin: '',
	normalMax: '',
	normalMin: '',
	enabled: true,
	icon: 'el-icon-odometer',
	parameterLoading: false,
	saveLoading: false,
	deleteLoading: false,
	parameterFields: [],
}

export default {
	name: 'VoltageRelationConfig',
	components: {
		CollectorSelectDialog,
	},
	data() {
		return {
			collectorDialogVisible: false,
			collectorLoading: false,
			saveConfigLoading: false,
			subDeviceLoading: false,
			selectedCollector: {
				id: this.$route.query.collectorDeviceId || '',
				name: '请选择采集器',
				code: this.$route.query.collectorDeviceNo || '-',
				status: '未选择',
				installLocation: '-',
				channelCount: 0,
				usedChannelCount: 0,
			},
			collectors: [],
			deviceTypeLoading: false,
			deviceTypeOptions: [],
			regulatorDetail: {},
			devices: [],
			channels: [
				...Array.from({ length: 8 }, (_, index) => index + 1),
				...Array.from({ length: 9 }, (_, index) => index + 11),
			].map(channelNo => {
				const code = String(channelNo)
				return {
					code,
					name: `CH${code}`,
					used: false,
					device: '未分配',
					parameter: '-',
				}
			}),
		}
	},
	computed: {
		regulatorId() {
			return this.$route.query.id
		},
		collectorDeviceNo() {
			return this.selectedCollector.code && this.selectedCollector.code !== '-' ? this.selectedCollector.code : ''
		},
		hasSelectedCollector() {
			return Boolean(
				this.selectedCollector.id || (this.selectedCollector.code && this.selectedCollector.code !== '-'),
			)
		},
		regulatorMetaText() {
			const detail = this.regulatorDetail || {}
			const brandModel = [detail.brand, detail.model].filter(Boolean).join(' ')
			return `调压器：${this.formatValue(detail.regulatorCode)}（${this.formatValue(
				brandModel,
			)}） | 所属调压柜：${this.formatValue(detail.cabinetNo)}`
		},
		channelStatusList() {
			return this.channels
				.filter(channel => Number(channel.code) <= 8)
				.map(channel => {
					const device = this.devices.find(item => item.slaveNo === channel.code)
					const deviceType = device ? this.getDeviceTypeLabel(device.type) : ''
					return {
						...channel,
						used: Boolean(device),
						device: deviceType || '未分配',
						parameter: device ? this.formatDeviceParameterInfo(device) : '-',
					}
				})
		},
	},
	mounted() {
		this.getRegulatorDetail()
		this.getDeviceTypes()
		if (this.hasSelectedCollector) {
			this.getCollectorDetail()
			this.getSubDevices()
		}
	},
	methods: {
		openCollectorDialog() {
			this.collectorDialogVisible = true
			this.getUnboundCollectors()
		},
		getRegulatorDetail() {
			if (!this.regulatorId) return
			apiGetPressureRegulatorDetail(this.regulatorId)
				.then(res => {
					this.regulatorDetail = res || {}
				})
				.catch(() => {
					this.regulatorDetail = {}
				})
		},
		getUnboundCollectors() {
			this.collectorLoading = true
			apiGetUnboundPressureRegulatorCollectors()
				.then(res => {
					const list = Array.isArray(res) ? res : res.records || res.list || res.data || []
					this.collectors = list.map(this.normalizeCollector)
				})
				.catch(() => {
					this.collectors = []
				})
				.finally(() => {
					this.collectorLoading = false
				})
		},
		getCollectorDetail() {
			if (!this.collectorDeviceNo) return
			apiGetPressureRegulatorCollector({
				deviceNo: this.collectorDeviceNo,
			})
				.then(res => {
					this.selectedCollector = this.normalizeCollector(res || {})
				})
				.catch(() => {})
		},
		getDeviceTypes() {
			this.deviceTypeLoading = true
			apiGetPressureRegulatorDeviceTypes()
				.then(res => {
					const list = Array.isArray(res) ? res : res.records || res.list || res.data || []
					this.deviceTypeOptions = list
						.filter(item => Number(item.nodeType) === 2)
						.map(this.normalizeDeviceType)
				})
				.catch(() => {
					this.deviceTypeOptions = []
				})
				.finally(() => {
					this.deviceTypeLoading = false
				})
		},
		normalizeDeviceType(item) {
			const value =
				item.deviceTypeId ||
				item.value ||
				item.deviceTypeCode ||
				item.typeCode ||
				item.type ||
				item.code ||
				item.id
			const label =
				item.label ||
				item.deviceTypeName ||
				item.typeName ||
				item.name ||
				item.deviceName ||
				value ||
				'未命名设备类型'
			return {
				...item,
				value,
				label,
			}
		},
		getDeviceTypeLabel(deviceTypeId) {
			return this.deviceTypeOptions.find(item => item.value === deviceTypeId)?.label || ''
		},
		handleDeviceTypeChange(device) {
			this.clearDeviceParameterFields(device)
			if (!device.type) return
			this.$set(device, 'parameterLoading', true)
			apiEditDeviceView({
				deviceTypeId: device.type,
				operate: 0,
			})
				.then(res => {
					const list = [...(res.baseProductAttributes || []), ...(res.deviceTypeAttributes || [])]
					const fields = list
						.filter(item => item.attributeKey && item.attributeKey !== 'subDeviceCount')
						.map(item => this.normalizeParameterField(item))
					this.$set(device, 'manufacturerId', res.manufacturerId)
					this.$set(device, 'connectType', res.nodeType)
					this.$set(device, 'parameterFields', fields)
					fields.forEach(item => {
						const defaultValue =
							item.defaultValue === undefined || item.defaultValue === null ? '' : item.defaultValue
						this.$set(device, item.attributeKey, defaultValue)
					})
				})
				.catch(() => {
					this.$set(device, 'parameterFields', [])
				})
				.finally(() => {
					this.$set(device, 'parameterLoading', false)
				})
		},
		getSubDevices() {
			if (!this.collectorDeviceNo) return
			this.subDeviceLoading = true
			apiGetPressureRegulatorSubDevices({
				collectorDeviceNo: this.collectorDeviceNo,
				includeBaseAttributes: true,
			})
				.then(res => {
					const list = Array.isArray(res) ? res : res.records || res.list || res.data || []
					this.devices = list.map(this.normalizeSubDevice)
				})
				.catch(() => {
					this.devices = []
				})
				.finally(() => {
					this.subDeviceLoading = false
				})
		},
		normalizeSubDevice(item) {
			const fields = this.getSubDeviceParameterFields(item)
			const device = {
				...item,
				id: item.iotDeviceId || item.deviceId || item.id || Date.now() + Math.random(),
				type: item.deviceTypeId || item.type || '',
				designNo: item.designNo || '',
				slaveNo: String(item.slaveNo || item.subDeviceNo || ''),
				connectType: item.connectType || item.nodeType || 2,
				parameterFields: fields,
			}
			fields.forEach(field => {
				const value = this.getSubDeviceDynamicValue(item, field)
				this.$set(device, field.attributeKey, value)
			})
			return device
		},
		getSubDeviceParameterFields(item) {
			const baseAttributes = item.dynamicBaseAttributes || item.baseProductAttributes || item.baseAttributes || []
			const deviceTypeAttributes = item.deviceTypeAttributes || item.typeAttributes || []
			return [...baseAttributes, ...deviceTypeAttributes]
				.filter(attr => attr.attributeKey && attr.attributeKey !== 'subDeviceCount')
				.map(attr => this.normalizeParameterField(attr))
		},
		getSubDeviceDynamicValue(item, field) {
			const dynamicContent = item.dynamicContent || {}
			const value =
				item[field.attributeKey] ?? dynamicContent[field.attributeKey] ?? field.value ?? field.defaultValue
			return value === undefined || value === null ? '' : value
		},
		clearDeviceParameterFields(device) {
			;(device.parameterFields || []).forEach(item => {
				this.$delete(device, item.attributeKey)
			})
			this.$set(device, 'parameterFields', [])
		},
		normalizeParameterField(item) {
			let interactiveTranslationDes = item.interactiveTranslationDes || {}
			if (typeof interactiveTranslationDes === 'string') {
				try {
					interactiveTranslationDes = JSON.parse(interactiveTranslationDes)
				} catch (e) {
					interactiveTranslationDes = {}
				}
			}
			return {
				attributeKey: item.attributeKey,
				name: item.name,
				valueType: String(item.valueType),
				unitname: item.unitname || item.unitName || item.unit,
				defaultValue: item.defaultValue,
				value: item.value,
				interactiveTranslationDes,
				rules: this.getParameterRules(item),
			}
		},
		getParameterRules(item) {
			const rules = []
			const trigger = String(item.valueType) === '2' || String(item.valueType) === '1' ? 'change' : 'blur'
			if (item.optional == 1) {
				rules.push({
					required: true,
					message: `${item.name}不可为空`,
					trigger,
				})
			}
			if (item.lengthCheck) {
				rules.push({
					max: Number(item.lengthCheck),
					message: `${item.name}不能超过${item.lengthCheck}位`,
					trigger: 'blur',
				})
			}
			const inputCheck = this.normalizeInputCheck(item.inputCheck)
			if (inputCheck?.reg) {
				try {
					rules.push({
						pattern: new RegExp(inputCheck.reg),
						message: inputCheck.regMsg || `${item.name}格式不正确`,
						trigger: 'blur',
					})
				} catch (e) {
					return rules
				}
			}
			return rules
		},
		normalizeInputCheck(inputCheck) {
			if (!inputCheck) return null
			if (typeof inputCheck !== 'string') return inputCheck
			try {
				return JSON.parse(inputCheck)
			} catch (e) {
				return null
			}
		},
		normalizeCollector(item) {
			const code = item.code || item.collectorDeviceNo || item.deviceNo || item.deviceCode || item.no || '-'
			const name = item.name || item.collectorName || item.deviceName || item.deviceTypeName || '未命名采集器'
			return {
				...item,
				id: item.deviceId || item.id || item.collectorDeviceId || item.collectorId || code,
				name,
				code,
				status: item.statusName || item.status || item.onlineStatusName || item.onlineStatus || '-',
				installLocation: item.installLocation || item.installAddress || item.address || item.location || '-',
				channelCount: item.channelCount || item.channelsCount || item.totalChannelCount || 0,
				usedChannelCount:
					item.subDeviceCount ||
					item.usedChannelCount ||
					item.usedChannelsCount ||
					item.boundChannelCount ||
					0,
			}
		},
		handleCollectorSelect(collector) {
			this.selectedCollector = { ...collector }
			this.getSubDevices()
		},
		handleBack() {
			this.$router.back()
		},
		handleCancel() {
			this.$router.back()
		},
		handleAddDevice() {
			const id = Date.now()
			this.devices.push({
				...deviceTemplate,
				parameterFields: [],
				id,
			})
		},
		handleRemoveDevice(device, index) {
			if (!device.deviceId) {
				this.devices.splice(index, 1)
				return
			}
			this.$confirm('确定删除当前关联子设备吗？', '提示', {
				confirmButtonText: '确定',
				cancelButtonText: '取消',
				type: 'warning',
			})
				.then(() => {
					this.$set(device, 'deleteLoading', true)
					return apiDeleteDevice(device.deviceId)
				})
				.then(() => {
					this.devices.splice(index, 1)
					this.$message.success('删除成功')
				})
				.catch(() => {})
				.finally(() => {
					this.$set(device, 'deleteLoading', false)
				})
		},
		handleSaveDevice(device) {
			if (!this.hasSelectedCollector) {
				this.$message.warning('请先选择采集器')
				return
			}
			if (this.isDuplicateChannel(device)) {
				this.$message.warning('通道选择不能重复')
				return
			}
			const formRef = this.getDeviceFormRef(device)
			if (!formRef) return
			formRef.validate(valid => {
				if (!valid) return
				this.$set(device, 'saveLoading', true)
				const request = device.deviceId ? apiChangeDevice : apiAddDevice
				request(this.buildDevicePayload(device))
					.then(res => {
						const deviceId = res?.deviceId || res?.id || device.deviceId
						if (deviceId) this.$set(device, 'deviceId', deviceId)
						this.$message.success('保存成功')
					})
					.catch(() => {})
					.finally(() => {
						this.$set(device, 'saveLoading', false)
					})
			})
		},
		getDeviceFormRef(device) {
			const ref = this.$refs[`deviceForm-${device.id}`]
			return Array.isArray(ref) ? ref[0] : ref
		},
		buildDevicePayload(device) {
			const dynamicContent = {}
			device.parameterFields.forEach(item => {
				const value = device[item.attributeKey]
				if (item.valueType === '1') {
					dynamicContent[item.attributeKey] = isBlank(value) ? null : this.dayjs(value).format('YYYY-MM-DD')
				} else {
					dynamicContent[item.attributeKey] = isBlank(value) ? null : value
				}
			})
			const payload = {
				dynamicContent,
				deviceStatus: 1,
				deviceTypeId: device.type,
				deviceNo: this.selectedCollector.code,
				manufacturerId: device.manufacturerId,
				connectType: device.connectType || 2,
				skipInstallCheck: true,
			}
			if (this.selectedCollector.id) payload.gatewayDeviceId = this.selectedCollector.id
			if (device.deviceId) payload.deviceId = device.deviceId
			payload.designNo = device.designNo
			if (!isBlank(device.slaveNo)) payload.slaveNo = device.slaveNo
			if (!isBlank(device.installDate)) payload.installDate = this.dayjs(device.installDate).format('YYYY-MM-DD')
			if (!isBlank(device.address)) payload.address = device.address
			return payload
		},
		isChannelSelectedByOtherDevice(channelCode, device) {
			return this.devices.some(item => item.id !== device.id && item.slaveNo === channelCode)
		},
		isDuplicateChannel(device) {
			return Boolean(device.slaveNo && this.isChannelSelectedByOtherDevice(device.slaveNo, device))
		},
		canDeleteDevice(device) {
			return !device.deviceId || Number(device.deviceStatus) === 0
		},
		isSavedDevice(device) {
			return Boolean(device.deviceId)
		},
		formatDeviceParameterInfo(device) {
			const fields = device.parameterFields || []
			if (!fields.length) return '-'
			const collectParameter = fields.find(field => field.name === '采集参数')
			if (!collectParameter) return '-'
			const value = device[collectParameter.attributeKey] || collectParameter.value
			return isBlank(value) ? '-' : value
		},
		formatValue(value) {
			return value === null || value === undefined || value === '' ? '--' : value
		},
		getUnsavedNewDeviceTips() {
			return this.devices
				.map((device, index) => ({
					device,
					index,
				}))
				.filter(item => !item.device.deviceId)
				.map(({ device, index }) => {
					const deviceType = this.getDeviceTypeLabel(device.type)
					const designNo = device.designNo ? `，外观编号：${device.designNo}` : ''
					const typeText = deviceType ? `，设备类型：${deviceType}` : ''
					return `子设备${index + 1}${designNo}${typeText}`
				})
		},
		handleSave() {
			if (!this.hasSelectedCollector) {
				this.$message.warning('请先选择采集器')
				return
			}
			if (!this.regulatorId) {
				this.$message.warning('缺少调压器ID')
				return
			}
			const unsavedNewDeviceTips = this.getUnsavedNewDeviceTips()
			if (unsavedNewDeviceTips.length) {
				this.$message.warning(`存在新增子设备未保存，请先保存：${unsavedNewDeviceTips.join('；')}`)
				return
			}
			this.saveConfigLoading = true
			apiBindPressureRegulatorCollector(this.regulatorId, {
				collectorDeviceNo: this.collectorDeviceNo,
			})
				.then(() => {
					this.$message.success('保存成功')
					this.$store.dispatch('account/changeStatus', true)
					this.$router.push({ name: 'voltageAccount' })
				})
				.catch(() => {})
				.finally(() => {
					this.saveConfigLoading = false
				})
		},
	},
}
</script>

<style lang="scss" scoped>
.relation-config {
	--page-bg: #eef4f8;
	--panel-bg: rgba(255, 255, 255, 0.96);
	--panel-border: #dbe5ee;
	--panel-shadow: 0 18px 38px rgba(15, 23, 42, 0.05);
	--border: #e5e7eb;
	--title: #0f172a;
	--text: #334155;
	--muted: #64748b;
	--primary: #2563eb;
	--blue-soft: #eff6ff;

	height: 100%;
	display: flex;
	flex-direction: column;
	background: radial-gradient(circle at top right, rgba(59, 130, 246, 0.12), transparent 26%),
		radial-gradient(circle at top left, rgba(14, 165, 233, 0.14), transparent 20%),
		linear-gradient(180deg, #f5f9fc 0%, var(--page-bg) 100%);
	color: var(--text);
	font-family: 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif;
}

.relation-config__scroll {
	flex: 1;
	height: 0;
	display: flex;
	flex-direction: column;
	gap: 12px;
	overflow-y: auto;
	padding: 12px;
}

.page-title-bar {
	position: relative;
	overflow: hidden;
	display: flex;
	align-items: center;
	gap: 16px;
	margin-bottom: 0;
	padding: 24px 28px 22px;
	border: 1px solid var(--panel-border);
	border-radius: 12px;
	backdrop-filter: blur(12px);
	background: linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(248, 251, 255, 0.94)), var(--panel-bg);
	flex-shrink: 0;

	h1 {
		margin: 0;
		font-size: 22px;
		font-weight: 700;
		letter-spacing: 0.01em;
		color: var(--title);
	}

	p {
		margin: 0;
		font-size: 14px;
		line-height: 1.6;
		color: var(--muted);
	}
}

.page-title-bar__glow {
	position: absolute;
	width: 280px;
	height: 280px;
	border-radius: 50%;
	filter: blur(18px);
	opacity: 0.25;
	pointer-events: none;
}

.page-title-bar__glow--left {
	left: -120px;
	top: -120px;
	background: rgba(37, 99, 235, 0.22);
}

.page-title-bar__glow--right {
	right: -100px;
	top: -120px;
	background: rgba(14, 165, 233, 0.22);
}

.page-title-bar__content {
	position: relative;
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.back-btn {
	position: relative;
	width: 42px;
	height: 42px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	padding: 0;
	border: 1px solid #d7e1ea;
	border-radius: 999px;
	background: rgba(255, 255, 255, 0.92);
	color: #334155;
	cursor: pointer;
	transition: all 0.2s ease;

	&:hover {
		color: var(--primary);
		border-color: #bfd4ff;
		background: #f8fbff;
		transform: translateY(-1px);
	}

	i {
		font-size: 16px;
	}
}

.notice-card,
.config-section {
	border: 1px solid var(--border);
	border-radius: 12px;
	background: var(--panel-bg);
}

.notice-card {
	display: flex;
	align-items: flex-start;
	gap: 12px;
	margin-bottom: 0;
	padding: 16px;
	border-color: #dbeafe;
	background: var(--blue-soft);
	color: #1e40af;

	i {
		font-size: 20px;
		line-height: 24px;
		color: var(--primary);
	}

	strong {
		display: block;
		margin-bottom: 2px;
		font-size: 14px;
		line-height: 20px;
	}

	p {
		margin: 0;
		font-size: 13px;
		line-height: 20px;
	}
}

.config-section {
	margin-bottom: 0;
	padding: 20px;
}

.section-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	margin-bottom: 16px;
}

.section-title {
	display: flex;
	align-items: center;
	gap: 8px;
	font-size: 18px;
	line-height: 24px;
	font-weight: 700;
	color: var(--title);
}

.section-icon {
	width: 20px;
	height: 20px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	border-radius: 4px;
	font-size: 12px;
}

.section-icon--purple {
	background: #f3e8ff;
	color: #9333ea;
}

.section-icon--blue {
	background: #dbeafe;
	color: var(--primary);
}

.section-icon--green {
	background: #dcfce7;
	color: #16a34a;
}

.collector-section .section-title,
.channel-section .section-title {
	margin-bottom: 16px;
}

.collector-select {
	min-height: 48px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	padding: 0 16px;
	border: 1px solid var(--border);
	border-radius: 8px;
	background: #fff;
	cursor: pointer;
	outline: none;
	transition: all 0.2s ease;

	&:hover,
	&:focus {
		border-color: #93c5fd;
		box-shadow: 0 8px 20px rgba(37, 99, 235, 0.08);
	}
}

.collector-main,
.collector-meta {
	display: flex;
	align-items: center;
	gap: 8px;
}

.collector-code {
	font-size: 14px;
	font-weight: 600;
	color: var(--primary);
}

.collector-name {
	font-size: 14px;
	font-weight: 600;
	color: var(--title);
}

.collector-meta {
	flex-wrap: wrap;
	justify-content: flex-end;
	font-size: 13px;
	color: var(--muted);

	span + span {
		margin-left: 8px;
	}

	i {
		margin-left: 8px;
		color: #9ca3af;
	}
}

.status-tag {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 35px;
	height: 18px;
	padding: 0 6px;
	border-radius: 999px;
	font-size: 11px;
	line-height: 14px;
	font-weight: 600;
	white-space: nowrap;
}

.status-tag--success {
	background: #dcfce7;
	color: #16a34a;
}

.status-tag--blue {
	background: #dbeafe;
	color: var(--primary);
}

.device-list {
	display: flex;
	flex-direction: column;
	gap: 16px;
}

.add-device-btn:hover {
	background: #1d4ed8;
	border-color: #1d4ed8;
}

.device-empty {
	min-height: 180px;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-direction: column;
	gap: 12px;
	padding: 32px 0;
	border: 1px dashed #cbd5e1;
	border-radius: 8px;
	background: #f8fafc;
}

.device-empty ::v-deep .gc-blank-page {
	margin-top: 0;
}

.device-empty ::v-deep .gc-blank-page .no-data-img {
	width: 120px;
	height: auto;
}

.device-card {
	padding: 16px;
	border: 1px solid var(--border);
	border-radius: 8px;
	background: #fff;
}

.device-card__header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	margin-bottom: 0;
}

.device-card__title {
	font-size: 15px;
	line-height: 22px;
	font-weight: 700;
	color: var(--title);
}

.device-title {
	display: flex;
	align-items: center;
	gap: 8px;
	min-width: 0;

	h2 {
		margin: 0;
		font-size: 16px;
		line-height: 24px;
		font-weight: 700;
		color: var(--title);
	}

	p {
		margin: 0;
		font-size: 13px;
		line-height: 18px;
		color: var(--muted);
	}
}

.device-icon {
	width: 40px;
	height: 40px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	border-radius: 8px;
	background: #dbeafe;
	color: var(--primary);
	font-size: 18px;
}

.device-actions {
	display: flex;
	align-items: center;
	gap: 8px;
	flex-shrink: 0;
}

.device-save-btn {
	min-width: 32px;
	width: 32px;
	height: 32px;
	padding: 0;
	border: 0;
}

.delete-btn {
	min-width: 32px;
	width: 32px;
	height: 32px;
	padding: 0;
	border: 0;
	background: #fef2f2;
	color: #ef4444;
}

.delete-btn:hover,
.delete-btn:focus {
	background: #fee2e2;
	color: #dc2626;
}

.device-form {
	display: grid;
	grid-template-columns: repeat(4, minmax(0, 1fr));
	gap: 24px;
	margin-bottom: 0;
}

.device-form ::v-deep .el-form-item__label {
	height: auto;
	padding-bottom: 0;
	line-height: 20px;
}

.field,
.parameter-field {
	display: flex;
	flex-direction: column;
	gap: 6px;

	label {
		font-size: 14px;
		line-height: 20px;
		font-weight: 600;
		color: var(--text);
	}
}

.parameter-field ::v-deep .el-date-editor {
	width: 100%;
}

.parameter-grid ::v-deep .el-form-item {
	margin-bottom: 4px;
}

.parameter-grid ::v-deep .el-form-item__label {
	display: flex;
	align-items: center;
	height: auto;
	padding-bottom: 2px;
	line-height: 18px;
}

.parameter-config {
	padding-top: 0;
	border-top: 0;
}

.parameter-config__title {
	margin-bottom: 12px;
	font-size: 14px;
	line-height: 20px;
	font-weight: 600;
	color: var(--text);
}

.parameter-grid {
	display: grid;
	grid-template-columns: repeat(5, minmax(0, 1fr));
	column-gap: 24px;
	row-gap: 0;
}

.channel-list {
	display: grid;
	grid-template-columns: repeat(8, minmax(120px, 1fr));
	gap: 16px;
	overflow-x: auto;
	padding-bottom: 2px;
}

.channel-card {
	min-height: 86px;
	padding: 12px;
	border: 1px solid var(--border);
	border-radius: 8px;
	background: #fff;

	&.is-used {
		background: #f9fafb;
	}

	p {
		margin: 8px 0 2px;
		font-size: 12px;
		line-height: 16px;
		color: var(--muted);
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	small {
		display: block;
		font-size: 11px;
		line-height: 14px;
		color: #94a3b8;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
}

.channel-card__top {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;

	strong {
		font-size: 14px;
		line-height: 20px;
		color: var(--title);
	}
}

.footer-actions {
	display: flex;
	justify-content: center;
	gap: 12px;
	margin: 12px 12px 12px;
	padding: 12px 24px;
	background: var(--panel-bg);
	border: 1px solid var(--panel-border);
	border-radius: 12px;
	backdrop-filter: blur(12px);
	flex-shrink: 0;

	.cancel-btn {
		width: 120px;
		min-width: 120px;
		height: 38px;
		margin: 0;
		border-radius: 10px;
		border-color: #d7dee8;
		color: #475569;
	}

	.save-btn {
		width: 120px;
		min-width: 120px;
		height: 38px;
		margin: 0;
		border-radius: 10px;
	}
}

::v-deep .el-button:not(.delete-btn):not(.device-save-btn) {
	min-width: 96px;
	height: 40px;
	border-radius: 8px;
}

::v-deep .el-button--primary {
	background: var(--primary);
	border-color: var(--primary);
}

.footer-actions ::v-deep .el-button--primary {
	background: var(--primary);
	border-color: var(--primary);
	color: #fff;
}

.footer-actions ::v-deep .el-button--primary:hover,
.footer-actions ::v-deep .el-button--primary:focus {
	background: #1d4ed8;
	border-color: #1d4ed8;
	color: #fff;
}

::v-deep .device-save-btn.el-button--primary {
	background: var(--blue-soft);
	border-color: #bfdbfe;
	color: var(--primary);
}

::v-deep .device-save-btn.el-button--primary:hover,
::v-deep .device-save-btn.el-button--primary:focus {
	background: #dbeafe;
	border-color: #93c5fd;
	color: #1d4ed8;
}

::v-deep .el-input__inner,
::v-deep .el-select .el-input__inner {
	height: 40px;
	border-radius: 8px;
	border-color: var(--border);
	color: var(--title);
}

::v-deep .el-select {
	width: 100%;
}

@media (max-width: 1280px) {
	.device-form {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}

	.parameter-grid {
		grid-template-columns: repeat(3, minmax(0, 1fr));
	}
}

@media (max-width: 900px) {
	.relation-config__scroll {
		padding: 12px;
	}

	.page-title-bar {
		align-items: flex-start;
		flex-direction: row;
		padding: 24px 28px 22px;
	}

	.collector-select,
	.section-header,
	.device-card__header {
		align-items: flex-start;
		flex-direction: column;
	}

	.collector-meta {
		justify-content: flex-start;
	}

	.device-form,
	.parameter-grid {
		grid-template-columns: 1fr;
		gap: 12px;
	}

	.footer-actions {
		padding: 12px 16px 16px;
	}
}
</style>
